// src/pages/backoffice/CategoryItems.jsx
import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import { getApiClient } from "../../libs/axiosInstance";
import Wrapper from "../_Wrapper";
import { getCurrencySymbol } from "../../libs/countryData";

const axios = getApiClient();

export default function CategoryItems({menuId}) {
const categoryId = menuId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [search, setSearch] = useState("");
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);

  useEffect(() => {
    loadAll();
  }, [categoryId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [catRes, itemsRes] = await Promise.all([
        axios.get(`/api/myshop/categories/${categoryId}`),
        axios.get(`/api/myshop/items`),
      ]);

      const cat = catRes.data.cat || catRes.data;
      const items = itemsRes.data.items || [];

      setCategory(cat);
      setAllItems(items);
      setSelectedIds((cat.itemIds || []).map(String));
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }

  const filteredAvailableItems = useMemo(() => {
    let list = allItems;
    if (showAvailableOnly) {
      list = list.filter((it) => it.available);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((it) => it.name.toLowerCase().includes(q));
    }
    return list;
  }, [allItems, showAvailableOnly, search]);

  const selectedItems = useMemo(() => {
    const map = new Map(allItems.map((it) => [String(it._id), it]));
    return selectedIds
      .map((id) => map.get(id))
      .filter(Boolean); // keep only existing
  }, [selectedIds, allItems]);

  function toggleSelect(itemId) {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(itemId)) {
        s.delete(itemId);
      } else {
        s.add(itemId);
      }
      return Array.from(s);
    });
  }

  async function handleSave() {
    try {
      await axios.put(`/api/myshop/categories/${categoryId}`, {
        itemIds: selectedIds,
      });
      Swal.fire("Saved", "Category items updated", "success");
      //navigate(-1);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save", "error");
    }
  }

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <div className="spinner-border" />
      </div>
    );
  }

  return (
    <Wrapper>
        <div className="container py-3">
        {/* header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
            <h4 className="m-0" style={{ fontSize: "1.05rem" }}>
                Items for: {category?.name}
            </h4>
            {category?.subtitle ? (
                <div style={{ fontSize: "0.75rem", color: "#666" }}>
                {category.subtitle}
                </div>
            ) : null}
            </div>
            <div>
            <button
                className="btn btn-light btn-sm me-2"
                onClick={() => navigate(-1)}
            >
                Back
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave}>
                Save
            </button>
            </div>
        </div>

        {/* 2-column layout */}
        <div className="row">
            {/* left: all items */}
            <div className="col-md-7 mb-3">
            <div className="card">
                <div className="card-header py-2 d-flex gap-2 align-items-center">
                <input
                    className="form-control form-control-sm"
                    placeholder="Search items…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className="form-check form-check-sm ms-2">
                    <input
                        id="availOnlyChk"
                    className="form-check-input"
                    type="checkbox"
                    checked={showAvailableOnly}
                    onChange={(e) => setShowAvailableOnly(e.target.checked)}
                    style={{ marginTop: "4px" }}
                    />
                    <label
                    htmlFor="availOnlyChk"
                    className="form-check-label"
                    style={{ fontSize: "0.7rem" }}
                    >
                    Available only
                    </label>
                </div>
                </div>
                <div
                className="card-body p-0"
                style={{ maxHeight: "58vh", overflowY: "auto" }}
                >
                {filteredAvailableItems.length === 0 ? (
                    <div className="p-3 text-center text-muted" style={{ fontSize: "0.8rem" }}>
                    No items found.
                    </div>
                ) : (
                    <ul className="list-group list-group-flush">
                    {filteredAvailableItems.map((it) => {
                        const isSelected = selectedIds.includes(String(it._id));
                        return (
                        <li
                            key={it._id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleSelect(String(it._id))}
                        >
                            <div>
                            <div
                                style={{
                                fontWeight: 500,
                                fontSize: "0.8rem",
                                }}
                            >
                                {it.name}
                            </div>
                            <div
                                style={{
                                fontSize: "0.7rem",
                                color: "#7d7d7d",
                                }}
                            >
                                {getCurrencySymbol()}{Number(it.price || 0).toFixed(2)}{" "}
                                {!it.available && (
                                <span className="text-danger ms-1">(unavailable)</span>
                                )}
                            </div>
                            </div>
                            <div>
                            {isSelected ? (
                                <span className="badge bg-primary">Added</span>
                            ) : (
                                <span className="badge bg-light text-muted">Add</span>
                            )}
                            </div>
                        </li>
                        );
                    })}
                    </ul>
                )}
                </div>
            </div>
            </div>

            {/* right: selected */}
            <div className="col-md-5 mb-3">
            <div className="card">
                <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <div style={{ fontSize: "0.8rem", fontWeight: 500 }}>
                    Selected ({selectedItems.length})
                </div>
                {selectedItems.length > 0 && (
                    <button
                    className="btn btn-link btn-sm text-danger"
                    onClick={() => setSelectedIds([])}
                    >
                    Clear
                    </button>
                )}
                </div>
                <div
                className="card-body p-0"
                style={{ maxHeight: "58vh", overflowY: "auto" }}
                >
                {selectedItems.length === 0 ? (
                    <div className="p-3 text-center text-muted" style={{ fontSize: "0.8rem" }}>
                    Nothing selected yet.
                    </div>
                ) : (
                    <ul className="list-group list-group-flush">
                    {selectedItems.map((it) => (
                        <li
                        key={it._id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                        >
                        <div>
                            <div style={{ fontSize: "0.8rem", fontWeight: 500 }}>
                            {it.name}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#666" }}>
                            {getCurrencySymbol()}{Number(it.price || 0).toFixed(2)}
                            </div>
                        </div>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => toggleSelect(String(it._id))}
                        >
                            remove
                        </button>
                        </li>
                    ))}
                    </ul>
                )}
                </div>
            </div>
            </div>
        </div>
        </div>
    </Wrapper>
  );
}
